import { Component, AfterViewInit, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import AOS from 'aos';
import { RelevantTechgeekComponent } from '../relevant-techgeek/relevant-techgeek.component';
import { ResourcesService } from '../../../services/resources.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

interface ArticlePost {
  id: number;
  title: string;
  image: string;
  slug?: string;
}
@Component({
  selector: 'app-techgeek-3',
  imports: [CommonModule, RelevantTechgeekComponent, ReactiveFormsModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './techgeek-3.component.html',
  styleUrl: './techgeek-3.component.scss'
})
export class Techgeek3Component implements AfterViewInit, OnInit, OnDestroy {
  isModalOpen = false;
  isSubmitting = false;
  submitSuccess = false;
  errorMessage = '';
  downloadForm!: FormGroup;
  private jsonLdScript: any;

  readonly techGeekTitle = "Tech Geek Ed. 3";
  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }

  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {

      this.isSubmitting = true;
      this.errorMessage = '';

      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        TechGeekTitle: this.techGeekTitle
      };

      this.resourcesService.sendTechGeekEmail(payload).subscribe({
        next: (response: any) => {
          if (response && !response.IsError) {
            this.submitSuccess = true;
            this.isSubmitting = false;

            setTimeout(() => {
              this.closeModal();
            }, 4000);
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message;
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.errorMessage = "Server error. Please try again.";
        }
      });
    }
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngAfterViewInit() {
    AOS.init();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

  injectSchemaMarkup(): void {
    const techGeek3Schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-3",
          "url": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-3",
          "name": "Tech Geek Ed. 3 — Kalpita Technologies Tech Magazine",
          "description": "Kalpita Tech Geek Magazine Edition 3 explores technology innovations across wellness (PlantWave), dining (HoloLamp AR), AI creativity (Wonder Studio), and API system architecture.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".softwaredev-heading",
              ".takeaways-section .takeaway-item p",
              ".section-title"
            ]
          },
          "significantLink": ["https://heyzine.com/flip-book/8ff6f06592.html"],
          "datePublished": "2023-07-01",
          "dateModified": "2023-09-30",
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-3#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Tech Geek", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek" },
            { "@type": "ListItem", "position": 3, "name": "Tech Geek Ed. 3", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-3" }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Tech Geek Ed. 3: AR Menus, AI Animation, and API Differences",
          "articleBody": "Kalpita Tech Geek Magazine Edition 3 covers PlantWave meditation, HoloLamp AR dining, Wonder Studio AI, and a deep-dive comparison of SOAP vs REST vs GraphQL vs RPC API technologies.",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "associatedMedia": {
            "@type": "MediaObject",
            "contentUrl": "https://heyzine.com/flip-book/8ff6f06592.html",
            "name": "Tech Geek Ed. 3 — Digital Flipbook Edition"
          }
        },
        {
          "@type": "HowTo",
          "name": "How to Apply Trends from Tech Geek Ed. 3",
          "step": [
            { "@type": "HowToStep", "name": "Plant-Based Meditation", "text": "Connect PlantWave to sensors to record biorhythm-based music for relaxation." },
            { "@type": "HowToStep", "name": "Holographic Dining", "text": "Use HoloLamp AR menus to preview 3D illusions of food dishes before ordering." },
            { "@type": "HowToStep", "name": "AI Animation", "text": "Upload footage to Wonder Studio to automate professional CG and VFX animation." },
            { "@type": "HowToStep", "name": "Prompt Crafting", "text": "Master specific specs for tone, context, and output constraints for better ChatGPT results." },
            { "@type": "HowToStep", "name": "API Architecture", "text": "Evaluate SOAP for strict enterprise security versus GraphQL for client-driven data queries." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How does Wonder Studio simplify animation?",
              "acceptedAnswer": { "@type": "Answer", "text": "It is an AI platform that automates the CG/VFX animation process from regular camera footage, removing the need for complex studio equipment." }
            },
            {
              "@type": "Question",
              "name": "What are the main API differences?",
              "acceptedAnswer": { "@type": "Answer", "text": "REST is best for lightweight web services, while GraphQL offers flexibility to prevent over-fetching, and SOAP is preferred for high-security enterprise contracts." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(techGeek3Schema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  articles: ArticlePost[] = [
    {
      id: 1,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-one.webp'
    },
    {
      id: 2,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-two.webp'
    },
    {
      id: 3,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-three.webp'
    },
    {
      id: 4,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-four.webp'
    },
    {
      id: 5,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-five.webp'
    },
    {
      id: 6,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-six.webp'
    },
    {
      id: 7,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-seven.webp'
    },
    {
      id: 8,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-eight.webp'
    }
  ];
  onArticleClick(article: ArticlePost): void {
    console.log('Article clicked:', article);
    // Handle navigation to article detail page
  }

  trackByArticle(index: number, article: ArticlePost): number {
    return article.id;
  }
}
